import { sanitizeNullStrings } from "@/utils/sanitize";
import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Grid2,
	TextField,
	Typography,
} from "@mui/material";
import ContactPhoneOutlinedIcon from "@mui/icons-material/ContactPhoneOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import {
	DetailBody,
	DetailHeader,
	DetailSection,
} from "@/controls/detail-panel/DetailPanel";
import { EDITABLE_FIELD_SX } from "@/pages/reports/rollshop-operations/components/compactDialog";
import type { Supplier } from "../models/Supplier";

interface FormErrors {
	Name?: string;
}

interface SupplierDetailViewerProps {
	supplier: Supplier | null;
	existingSuppliers: Supplier[];
	isSaving: boolean;
	onSave: (supplier: Supplier) => void;
	onClose: () => void;
}

export const SupplierDetailViewer = ({
	supplier,
	existingSuppliers,
	isSaving,
	onSave,
	onClose,
}: SupplierDetailViewerProps) => {
	const [formData, setFormData] = useState<Supplier | null>(null);
	const [errors, setErrors] = useState<FormErrors>({});
	const [touched, setTouched] = useState<Record<string, boolean>>({});

	const isNewSupplier = !supplier?.Id;

	const validateForm = useMemo(() => {
		return (data: Supplier | null): FormErrors => {
			const newErrors: FormErrors = {};

			if (!data?.Name || data.Name.trim().length === 0) {
				newErrors.Name = "Name is required";
			} else {
				const isDuplicate = existingSuppliers.some(
					(s) =>
						s.Id !== data.Id &&
						s.Name.toLowerCase() === data.Name.trim().toLowerCase(),
				);
				if (isDuplicate) {
					newErrors.Name = "This name already exists";
				}
			}

			return newErrors;
		};
	}, [existingSuppliers]);

	const formErrors = useMemo(
		() => validateForm(formData),
		[formData, validateForm],
	);
	const isFormValid = Object.keys(formErrors).length === 0;
	const canCreate = isNewSupplier && isFormValid;

	useEffect(() => {
		if (supplier) {
			setFormData(sanitizeNullStrings({ ...supplier }));
		} else {
			setFormData(null);
		}
		setErrors({});
		setTouched({});
	}, [supplier]);

	const handleChange = (field: keyof Supplier, value: unknown) => {
		setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
	};

	const handleBlur = (field: keyof Supplier) => {
		setTouched((prev) => ({ ...prev, [field]: true }));
		if (formData) {
			setErrors(validateForm(formData));
		}
	};

	const handleBlurSave = (field: keyof Supplier, value: unknown) => {
		handleBlur(field);

		if (isNewSupplier) return;

		const updatedData = formData ? { ...formData, [field]: value } : null;
		const currentErrors = validateForm(updatedData);
		if (currentErrors[field as keyof FormErrors]) return;

		const originalValue = supplier?.[field];
		const normalizedOriginal = originalValue === null ? "" : originalValue;
		const normalizedNew = value === null ? "" : value;

		if (normalizedOriginal !== normalizedNew) {
			onSave({ ...formData!, [field]: value });
		}
	};

	const handleCheckboxChange = (field: keyof Supplier, value: unknown) => {
		handleChange(field, value);

		if (isNewSupplier) return;

		if (supplier?.[field] !== value) {
			onSave({ ...formData!, [field]: value });
		}
	};

	const handleCreate = () => {
		setTouched({ Name: true });
		setErrors(formErrors);

		if (formData && canCreate) {
			onSave(formData);
		}
	};

	if (!supplier) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
					color: "text.secondary",
				}}
			>
				<Typography>Select a supplier to view details</Typography>
			</Box>
		);
	}

	if (!formData) return null;

	return (
		<Box
			sx={{
				height: "100%",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			<DetailHeader
				title={isNewSupplier ? "New Supplier" : formData.Name}
				active={isNewSupplier ? undefined : supplier.Active}
				isSaving={isSaving}
				onClose={onClose}
				actions={
					isNewSupplier ? (
						<Button
							color="primary"
							variant="contained"
							size="small"
							onClick={handleCreate}
							disabled={!canCreate || isSaving}
						>
							{isSaving ? "CREATING..." : "CREATE"}
						</Button>
					) : undefined
				}
			/>

			<DetailBody>
				<Box
					sx={{
						flex: 1,
						minHeight: 0,
						overflow: "auto",
						display: "flex",
						flexDirection: "column",
						gap: 0.75,
					}}
				>
					<DetailSection title="Basic Information" icon={<InfoOutlinedIcon />}>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<TextField
									label="Name"
									value={formData.Name}
									onChange={(e) => handleChange("Name", e.target.value)}
									onBlur={(e) => handleBlurSave("Name", e.target.value)}
									fullWidth
									size="small"
									required
									error={touched.Name && !!errors.Name}
									helperText={touched.Name && errors.Name}
									sx={EDITABLE_FIELD_SX}
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<TextField
									label="Short Name"
									value={formData.ShortName ?? ""}
									onChange={(e) => handleChange("ShortName", e.target.value)}
									onBlur={(e) => handleBlurSave("ShortName", e.target.value)}
									fullWidth
									size="small"
									sx={EDITABLE_FIELD_SX}
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<TextField
									label="Number"
									value={formData.Number ?? ""}
									onChange={(e) => handleChange("Number", e.target.value)}
									onBlur={(e) => handleBlurSave("Number", e.target.value)}
									fullWidth
									size="small"
									sx={EDITABLE_FIELD_SX}
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							</Grid2>
							<Grid2 size={{ xs: 6, md: 3 }}>
								<FormControlLabel
									control={
										<Checkbox
											checked={formData.Active}
											onChange={(e) =>
												handleCheckboxChange("Active", e.target.checked)
											}
											size="small"
										/>
									}
									label="Active"
									sx={{ ml: 0 }}
								/>
							</Grid2>
						</Grid2>
					</DetailSection>

					<DetailSection title="Address" icon={<PlaceOutlinedIcon />}>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12 }}>
								<TextField
									label="Address 1"
									value={formData.Address1 ?? ""}
									onChange={(e) => handleChange("Address1", e.target.value)}
									onBlur={(e) => handleBlurSave("Address1", e.target.value)}
									fullWidth
									size="small"
									sx={EDITABLE_FIELD_SX}
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12 }}>
								<TextField
									label="Address 2"
									value={formData.Address2 ?? ""}
									onChange={(e) => handleChange("Address2", e.target.value)}
									onBlur={(e) => handleBlurSave("Address2", e.target.value)}
									fullWidth
									size="small"
									sx={EDITABLE_FIELD_SX}
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12 }}>
								<TextField
									label="Address 3"
									value={formData.Address3 ?? ""}
									onChange={(e) => handleChange("Address3", e.target.value)}
									onBlur={(e) => handleBlurSave("Address3", e.target.value)}
									fullWidth
									size="small"
									sx={EDITABLE_FIELD_SX}
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12 }}>
								<TextField
									label="Address 4"
									value={formData.Address4 ?? ""}
									onChange={(e) => handleChange("Address4", e.target.value)}
									onBlur={(e) => handleBlurSave("Address4", e.target.value)}
									fullWidth
									size="small"
									sx={EDITABLE_FIELD_SX}
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							</Grid2>
							<Grid2 size={{ xs: 6 }}>
								<TextField
									label="City"
									value={formData.City ?? ""}
									onChange={(e) => handleChange("City", e.target.value)}
									onBlur={(e) => handleBlurSave("City", e.target.value)}
									fullWidth
									size="small"
									sx={EDITABLE_FIELD_SX}
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							</Grid2>
							<Grid2 size={{ xs: 6 }}>
								<TextField
									label="State"
									value={formData.State ?? ""}
									onChange={(e) => handleChange("State", e.target.value)}
									onBlur={(e) => handleBlurSave("State", e.target.value)}
									fullWidth
									size="small"
									sx={EDITABLE_FIELD_SX}
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							</Grid2>
							<Grid2 size={{ xs: 6 }}>
								<TextField
									label="Zip"
									value={formData.Zip ?? ""}
									onChange={(e) => handleChange("Zip", e.target.value)}
									onBlur={(e) => handleBlurSave("Zip", e.target.value)}
									fullWidth
									size="small"
									sx={EDITABLE_FIELD_SX}
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							</Grid2>
							<Grid2 size={{ xs: 6 }}>
								<TextField
									label="Country"
									value={formData.Country ?? ""}
									onChange={(e) => handleChange("Country", e.target.value)}
									onBlur={(e) => handleBlurSave("Country", e.target.value)}
									fullWidth
									size="small"
									sx={EDITABLE_FIELD_SX}
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							</Grid2>
						</Grid2>
					</DetailSection>

					<DetailSection
						title="Contact Information"
						icon={<ContactPhoneOutlinedIcon />}
					>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 6 }}>
								<TextField
									label="Phone"
									value={formData.Phone ?? ""}
									onChange={(e) => handleChange("Phone", e.target.value)}
									onBlur={(e) => handleBlurSave("Phone", e.target.value)}
									fullWidth
									size="small"
									sx={EDITABLE_FIELD_SX}
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							</Grid2>
							<Grid2 size={{ xs: 6 }}>
								<TextField
									label="Fax"
									value={formData.Fax ?? ""}
									onChange={(e) => handleChange("Fax", e.target.value)}
									onBlur={(e) => handleBlurSave("Fax", e.target.value)}
									fullWidth
									size="small"
									sx={EDITABLE_FIELD_SX}
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							</Grid2>
							<Grid2 size={{ xs: 6 }}>
								<TextField
									label="Contact Name"
									value={formData.Contact ?? ""}
									onChange={(e) => handleChange("Contact", e.target.value)}
									onBlur={(e) => handleBlurSave("Contact", e.target.value)}
									fullWidth
									size="small"
									sx={EDITABLE_FIELD_SX}
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							</Grid2>
							<Grid2 size={{ xs: 6 }}>
								<TextField
									label="Contact Name 2"
									value={formData.ContactName2 ?? ""}
									onChange={(e) => handleChange("ContactName2", e.target.value)}
									onBlur={(e) => handleBlurSave("ContactName2", e.target.value)}
									fullWidth
									size="small"
									sx={EDITABLE_FIELD_SX}
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							</Grid2>
						</Grid2>
					</DetailSection>
				</Box>
			</DetailBody>
		</Box>
	);
};
